import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/shared/api/apiFetch';
import {
  medicalSectionsResponseSchema,
  medicalCheckEmployeesResponseSchema,
  getEmployeeCardResponseSchema,
  getExamDetailResponseSchema,
  listExamEventsResponseSchema,
  listExamCommentsResponseSchema,
  listExamDocumentsResponseSchema,
  summaryWidgetResponseSchema,
  riskWidgetResponseSchema,
  validateReferralResponseSchema,
  issueReferralResponseSchema,
  exportReferralResponseSchema,
  type MedicalSection,
  type MedicalCheckEmployeeRow,
  type MedicalCheckEmployeeDetail,
  type MedicalCheckRow,
  type UpcomingAction,
  type MedicalExamDetail,
  type MedicalExamEvent,
  type MedicalExamComment,
  type MedicalExamDocument,
  type SummaryWidget,
  type RiskWidget,
  type ValidateReferralResult,
  type IssueReferralResult,
  type ExportReferralResult,
} from '@/entities/medical/model/medicalChecksSchema';

// --- Sections (§5.1.1) ---

async function fetchMedicalSections(): Promise<MedicalSection[]> {
  const raw = await apiFetch<unknown>('/medical-checks/sections');
  const parsed = medicalSectionsResponseSchema.parse(raw);
  return parsed.sections;
}

export function useMedicalSections() {
  return useQuery({
    queryKey: ['medical-checks', 'sections'],
    queryFn: fetchMedicalSections,
    staleTime: 60_000,
  });
}

// --- Employees list (§5.2.1) ---

export const MEDICAL_EMPLOYEES_PAGE_SIZE = 10;

export interface MedicalEmployeesParams {
  q?: string;
  orgUnitId?: string;
  checkTypes?: string[];
  statuses?: string[];
  admissionStatuses?: string[];
  periodField?: 'last_check_date' | 'next_check_date';
  periodFrom?: string;
  periodTo?: string;
  preset?: 'overdue' | 'not_allowed';
  pageToken?: string;
}

export interface MedicalEmployeesResult {
  items: MedicalCheckEmployeeRow[];
  nextPageToken: string | null;
}

async function fetchMedicalEmployees(params: MedicalEmployeesParams): Promise<MedicalEmployeesResult> {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.orgUnitId) sp.set('org_unit_id', params.orgUnitId);
  if (params.checkTypes) sp.set('check_types', params.checkTypes.join(','));
  if (params.statuses) sp.set('statuses', params.statuses.join(','));
  if (params.admissionStatuses) sp.set('admission_statuses', params.admissionStatuses.join(','));
  if (params.periodField) sp.set('period_field', params.periodField);
  if (params.periodFrom) sp.set('period_from', params.periodFrom);
  if (params.periodTo) sp.set('period_to', params.periodTo);
  if (params.preset) sp.set('preset', params.preset);
  if (params.pageToken) sp.set('page_token', params.pageToken);

  const qs = sp.toString();
  const path = `/medical-checks/employees${qs ? `?${qs}` : ''}`;
  const raw = await apiFetch<unknown>(path);
  const parsed = medicalCheckEmployeesResponseSchema.parse(raw);
  return { items: parsed.employees, nextPageToken: parsed.next_page_token ?? null };
}

export type MedicalEmployeesListFilters = Omit<MedicalEmployeesParams, 'pageToken'>;

export function useMedicalEmployeesInfinite(filters: MedicalEmployeesListFilters) {
  return useInfiniteQuery({
    queryKey: ['medical-checks', 'employees', 'list', 'infinite', filters],
    queryFn: ({ pageParam }) => fetchMedicalEmployees({ ...filters, pageToken: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextPageToken ?? undefined,
  });
}

// --- Employee medical card (§5.3.1) ---

export interface EmployeeMedicalCard {
  employee: MedicalCheckEmployeeDetail;
  history: MedicalCheckRow[];
  upcomingActions: UpcomingAction[];
}

async function fetchEmployeeMedicalCard(employeeId: string): Promise<EmployeeMedicalCard> {
  const raw = await apiFetch<unknown>(`/medical-checks/employees/${employeeId}`);
  const parsed = getEmployeeCardResponseSchema.parse(raw);
  return {
    employee: parsed.employee,
    history: parsed.history,
    upcomingActions: parsed.upcoming_actions,
  };
}

export function useEmployeeMedicalCard(employeeId: string | null) {
  return useQuery({
    queryKey: ['medical-checks', 'employees', employeeId],
    queryFn: () => fetchEmployeeMedicalCard(employeeId!),
    enabled: !!employeeId,
    placeholderData: keepPreviousData,
  });
}

// --- Exam detail (§5.4.1) ---

async function fetchMedicalExamDetail(examId: string): Promise<MedicalExamDetail> {
  const raw = await apiFetch<unknown>(`/medical-checks/exams/${examId}`);
  const parsed = getExamDetailResponseSchema.parse(raw);
  return parsed.exam;
}

export function useMedicalExamDetail(examId: string | null) {
  return useQuery({
    queryKey: ['medical-checks', 'exams', examId],
    queryFn: () => fetchMedicalExamDetail(examId!),
    enabled: !!examId,
  });
}

// --- Exam events (§5.4.2) ---

async function fetchMedicalExamEvents(examId: string): Promise<MedicalExamEvent[]> {
  const raw = await apiFetch<unknown>(`/medical-checks/exams/${examId}/events`);
  return listExamEventsResponseSchema.parse(raw).events;
}

export function useMedicalExamEvents(examId: string | null) {
  return useQuery({
    queryKey: ['medical-checks', 'exams', examId, 'events'],
    queryFn: () => fetchMedicalExamEvents(examId!),
    enabled: !!examId,
  });
}

// --- Exam comments (§5.4.3) ---

async function fetchMedicalExamComments(examId: string): Promise<MedicalExamComment[]> {
  const raw = await apiFetch<unknown>(`/medical-checks/exams/${examId}/comments`);
  return listExamCommentsResponseSchema.parse(raw).comments;
}

export function useMedicalExamComments(examId: string | null) {
  return useQuery({
    queryKey: ['medical-checks', 'exams', examId, 'comments'],
    queryFn: () => fetchMedicalExamComments(examId!),
    enabled: !!examId,
  });
}

// --- Exam documents (§5.5.1) ---

async function fetchMedicalExamDocuments(examId: string): Promise<MedicalExamDocument[]> {
  const raw = await apiFetch<unknown>(`/medical-checks/exams/${examId}/documents`);
  return listExamDocumentsResponseSchema.parse(raw).documents;
}

export function useMedicalExamDocuments(examId: string | null) {
  return useQuery({
    queryKey: ['medical-checks', 'exams', examId, 'documents'],
    queryFn: () => fetchMedicalExamDocuments(examId!),
    enabled: !!examId,
  });
}

// --- Widgets (§US-2.10) ---

async function fetchSummaryWidget(): Promise<SummaryWidget> {
  const raw = await apiFetch<unknown>('/medical-checks/widgets/summary');
  return summaryWidgetResponseSchema.parse(raw).widget;
}

export function useMedicalSummaryWidget() {
  return useQuery({
    queryKey: ['medical-checks', 'widgets', 'summary'],
    queryFn: fetchSummaryWidget,
  });
}

async function fetchRiskWidget(): Promise<RiskWidget> {
  const raw = await apiFetch<unknown>('/medical-checks/widgets/risk');
  return riskWidgetResponseSchema.parse(raw).widget;
}

export function useMedicalRiskWidget(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['medical-checks', 'widgets', 'risk'],
    queryFn: fetchRiskWidget,
    enabled: options?.enabled ?? true,
  });
}

// --- Referral list ---

export interface MedicalReferralListItem {
  id: string;
  status: string;
  template_code: string;
  employees_count: number;
  created_at: string;
  issued_at: string | null;
}

async function fetchMedicalReferrals(): Promise<MedicalReferralListItem[]> {
  const raw = await apiFetch<{ referrals: MedicalReferralListItem[] }>('/medical-checks/referrals');
  return raw.referrals;
}

export function useMedicalReferrals() {
  return useQuery({
    queryKey: ['medical-checks', 'referrals'],
    queryFn: fetchMedicalReferrals,
    staleTime: 30_000,
  });
}

// --- Referral actions (§5.6) ---

async function validateReferral(referralId: string, strictMode = false): Promise<ValidateReferralResult> {
  const raw = await apiFetch<unknown>(`/medical-checks/referrals/${referralId}:validate`, {
    method: 'POST',
    body: { strict_mode: strictMode },
  });
  return validateReferralResponseSchema.parse(raw);
}

export function useValidateReferral() {
  return useMutation({
    mutationFn: ({ referralId, strictMode }: { referralId: string; strictMode?: boolean }) =>
      validateReferral(referralId, strictMode),
  });
}

async function issueReferral(referralId: string): Promise<IssueReferralResult> {
  const raw = await apiFetch<unknown>(`/medical-checks/referrals/${referralId}:issue`, {
    method: 'POST',
    body: {},
  });
  return issueReferralResponseSchema.parse(raw);
}

export function useIssueReferral() {
  return useMutation({
    mutationFn: (referralId: string) => issueReferral(referralId),
  });
}

async function exportReferral(referralId: string, format = 'json'): Promise<ExportReferralResult> {
  const raw = await apiFetch<unknown>(`/medical-checks/referrals/${referralId}:export`, {
    method: 'POST',
    body: { format },
  });
  return exportReferralResponseSchema.parse(raw);
}

export function useExportReferral() {
  return useMutation({
    mutationFn: ({ referralId, format }: { referralId: string; format?: string }) => exportReferral(referralId, format),
  });
}
